import { useAtomValue } from "jotai/react";
import {
  CalendarClockIcon,
  CircleHelpIcon,
  CreditCardIcon,
  EllipsisIcon,
  FilesIcon,
  LucideIcon,
  PhoneCallIcon,
  PillIcon,
  ShieldCheckIcon,
  StethoscopeIcon,
} from "lucide-react-native";

import { useOnboarding } from "@/context/OnboardingContext";
import {
  OnboardingConcernOptionEnum,
  OnboardingStepEnum,
} from "@/domain/onboarding";
import { useOnboardingEntryPointData } from "@/hooks/useOnboardingEntryPointData";
import { onboardingShortCodeDataAtom } from "@/stores/atoms";
import { OnboardingI18nPathType, OnboardingTextType } from "@/utils/onboarding";

type OptionType = {
  id: OnboardingConcernOptionEnum;
  optionI18nPath: OnboardingI18nPathType;
  icon: LucideIcon;
};

export type OnboardingConcernOptionType = {
  id: OnboardingConcernOptionEnum;
  text: OnboardingTextType;
  icon: LucideIcon;
};

// The concerns step's copy and the options offered for the current entry
// point, plus the option the user picked, independent of how a layout
// presents them.
//
// The picked option lives in the onboarding context, so the next steps step
// reads the same value.
export const useOnboardingConcernsContent = () => {
  const onboardingShortCodeData = useAtomValue(onboardingShortCodeDataAtom);
  const { getI18nKey, selectedConcernOption, setSelectedConcernOption } =
    useOnboarding();

  const {
    isAwarenessCampaign,
    isMedicareActive,
    isMedicaidActive,
    i18nKeyContext,
  } = useOnboardingEntryPointData(OnboardingStepEnum.CONCERNS);

  const selectOption = (optionId: OnboardingConcernOptionEnum) => {
    setSelectedConcernOption(optionId);
  };

  const getListOfOptions = (): OptionType[] => {
    const listOfOptions = [
      {
        id: OnboardingConcernOptionEnum.PAYING_FOR_DOCTOR,
        optionI18nPath: "payingForDoctor.option",
        icon: CreditCardIcon,
      },
      {
        id: OnboardingConcernOptionEnum.NOT_KNOWING_IM_COVERED,
        optionI18nPath: "notKnowingImCovered.option",
        icon: CircleHelpIcon,
      },
      {
        id: OnboardingConcernOptionEnum.PAPERWORK,
        optionI18nPath: "paperwork.option",
        icon: FilesIcon,
      },
      {
        id: OnboardingConcernOptionEnum.SOMETHING_ELSE,
        optionI18nPath: "somethingElse.option",
        icon: EllipsisIcon,
      },
    ] as OptionType[];

    if (onboardingShortCodeData?.entryPointType === "AGING_INTO_MEDICARE") {
      if (isAwarenessCampaign) {
        listOfOptions[0] = {
          id: OnboardingConcernOptionEnum.MISSING_ENROLLMENT_DEADLINE,
          optionI18nPath: "missingEnrolmentDeadline.option",
          icon: CreditCardIcon,
        };
        listOfOptions[1] = {
          id: OnboardingConcernOptionEnum.NOT_KNOWING_HOW_TO_GET_COVERED,
          optionI18nPath: "notKnowingHowToGetCovered.option",
          icon: CircleHelpIcon,
        };
        listOfOptions[2] = {
          id: OnboardingConcernOptionEnum.TOO_MANY_MEDICARE_ADS_AND_CALLS,
          optionI18nPath: "tooManyMedicareAdsAndCalls.option",
          icon: PhoneCallIcon,
        };
      } else {
        listOfOptions[0] = {
          id: OnboardingConcernOptionEnum.NOT_KNOWING_HOW_TO_GET_COVERED,
          optionI18nPath: "notKnowingHowToGetCovered.option",
          icon: CircleHelpIcon,
        };
        listOfOptions[1] = {
          id: OnboardingConcernOptionEnum.TOO_MANY_MEDICARE_ADS_AND_CALLS,
          optionI18nPath: "tooManyMedicareAdsAndCalls.option",
          icon: PhoneCallIcon,
        };
        listOfOptions[2] = {
          id: OnboardingConcernOptionEnum.KEEPING_MY_CURRENT_DOCTORS,
          optionI18nPath: "keepingMyCurrentDoctors.option",
          icon: StethoscopeIcon,
        };
      }
    }

    if (onboardingShortCodeData?.entryPointType === "SERVICE_INTRODUCTION") {
      if (isMedicaidActive) {
        listOfOptions[0] = {
          id: OnboardingConcernOptionEnum.SUBMITTING_RENEWALS,
          optionI18nPath: "submittingRenewals.option",
          icon: FilesIcon,
        };
        listOfOptions[1] = {
          id: OnboardingConcernOptionEnum.FIGURING_OUT_WHEN_TO_RENEW,
          optionI18nPath: "figuringOutWhenToRenew.option",
          icon: CalendarClockIcon,
        };
        listOfOptions[2] = {
          id: OnboardingConcernOptionEnum.NOT_KNOWING_IM_COVERED,
          optionI18nPath: "notKnowingImCovered.option",
          icon: CircleHelpIcon,
        };
      }

      if (isMedicareActive) {
        listOfOptions[0] = {
          id: OnboardingConcernOptionEnum.PART_A_AND_B,
          optionI18nPath: "partAB.option",
          icon: CreditCardIcon,
        };
        listOfOptions[1] = {
          id: OnboardingConcernOptionEnum.PART_A_B_AND_D,
          optionI18nPath: "partABD.option",
          icon: PillIcon,
        };
        listOfOptions[2] = {
          id: OnboardingConcernOptionEnum.PART_C,
          optionI18nPath: "partC.option",
          icon: ShieldCheckIcon,
        };
        listOfOptions[3] = {
          id: OnboardingConcernOptionEnum.IM_NOT_SURE,
          optionI18nPath: "imNotSure.option",
          icon: CircleHelpIcon,
        };
      }
    }

    if (
      onboardingShortCodeData?.entryPointType === "RECURRING_WORK_REQUIREMENT"
    ) {
      listOfOptions[0] = {
        id: OnboardingConcernOptionEnum.MEETING_REQUIREMENTS,
        optionI18nPath: "meetingRequirements.option",
        icon: FilesIcon,
      };
      listOfOptions[1] = {
        id: OnboardingConcernOptionEnum.FIGURING_OUT_WHEN_TO_SUBMIT_REQUIREMENTS,
        optionI18nPath: "figuringOutWhenToSubmitRequirements.option",
        icon: CalendarClockIcon,
      };
      listOfOptions[2] = {
        id: OnboardingConcernOptionEnum.NOT_KNOWING_IM_COVERED,
        optionI18nPath: "notKnowingImCovered.option",
        icon: CircleHelpIcon,
      };
    }

    if (onboardingShortCodeData?.entryPointType === "REDETERMINATION") {
      listOfOptions[0] = {
        id: OnboardingConcernOptionEnum.SUBMITTING_RENEWALS,
        optionI18nPath: "submittingRenewals.option",
        icon: FilesIcon,
      };
      listOfOptions[1] = {
        id: OnboardingConcernOptionEnum.FIGURING_OUT_WHEN_TO_RENEW,
        optionI18nPath: "figuringOutWhenToRenew.option",
        icon: CalendarClockIcon,
      };
      listOfOptions[2] = {
        id: OnboardingConcernOptionEnum.NOT_KNOWING_IM_COVERED,
        optionI18nPath: "notKnowingImCovered.option",
        icon: CircleHelpIcon,
      };
    }

    return listOfOptions;
  };

  const options: OnboardingConcernOptionType[] = getListOfOptions().map(
    ({ id, optionI18nPath, icon }) => ({
      id,
      text: { i18nKey: getI18nKey(optionI18nPath) },
      icon,
    }),
  );

  return {
    title: {
      i18nKey: getI18nKey("concerns.title"),
      i18nOptions: { context: i18nKeyContext },
    } satisfies OnboardingTextType,
    description: {
      i18nKey: getI18nKey("concerns.description"),
      i18nOptions: { context: i18nKeyContext },
    } satisfies OnboardingTextType,
    options,
    selectedOption: selectedConcernOption,
    selectOption,
  };
};
